import { BREADCRUMBS_DENSITY_OPTIONS } from '@/common/constants';
import { Env } from '@/common/types';
import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Footer, Navbar } from '@/components/global';
import { NAVBAR_FALLBACK } from '@/constants/navbarFallback';
import {
  PRODUCT_ROADMAP_2026_SEGMENT,
  PRODUCT_ROADMAP_ASSETS,
  PRODUCT_ROADMAP_AUTHOR,
  productRoadmapInlineVisualsBySection,
} from '@/constants/productRoadmap2026';
import {
  productRoadmapMessages,
  productRoadmapSections,
  type ProductRoadmapSectionKey,
} from '@/constants/productRoadmapMessages';
import { Typography } from '@eventbrite/marmalade';
import React, { useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import styles from './ProductRoadmapPage.module.scss';
import { renderProductRoadmapRichText } from './renderProductRoadmapRichText';

const HEADING_MD_SECTIONS = new Set<ProductRoadmapSectionKey>(['whatComesNext', 'acceleratedDev']);

const AUTHOR_VALUES = {
  authorFirstName: PRODUCT_ROADMAP_AUTHOR.firstName,
  authorFullName: `${PRODUCT_ROADMAP_AUTHOR.firstName} ${PRODUCT_ROADMAP_AUTHOR.lastName}`,
};

/** Render a message as a `<p>` with inline bold/links via renderProductRoadmapRichText. */
function P({
  formatMessage,
  msg,
  values,
}: {
  formatMessage: IntlShape['formatMessage'];
  msg: { id: string; defaultMessage?: string };
  values?: Record<string, string>;
}) {
  return renderProductRoadmapRichText(formatMessage(msg, values) as string);
}

/**
 * Same white frame + square media as the former end-of-section block; videos/images from
 * `productRoadmapInlineVisualsBySection`.
 */
const ProductRoadmapSectionVisual: React.FC<{ sectionKey: string; visualIndex: number }> = ({
  sectionKey,
  visualIndex,
}) => {
  const chain = productRoadmapInlineVisualsBySection[sectionKey] ?? [];
  const asset = chain[visualIndex] ?? chain[chain.length - 1];
  if (!asset) return null;

  return (
    <div className={styles.visualPlaceholder}>
      <div className={styles.sectionImageFrame}>
        <div className={styles.sectionImageWrap}>
          {asset.kind === 'video' ? (
            <video className={styles.sectionVideo} autoPlay loop muted playsInline preload="auto">
              <source src={asset.src} type="video/mp4" />
            </video>
          ) : (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={asset.src}
              alt=""
              className={styles.sectionImage}
              width={800}
              height={800}
              loading="lazy"
            />
          )}
        </div>
      </div>
    </div>
  );
};

/** Closing block: each paragraph is a separate message so line breaks render correctly. */
const ProductRoadmapClosingBody: React.FC<{ ebOrigin: string }> = ({ ebOrigin }) => {
  const { formatMessage } = useIntl();
  const m = productRoadmapMessages;
  return (
    <>
      <p>
        {formatMessage(m.closingPara1, {
          productUpdates: (chunks) => <a href={`${ebOrigin}/product-updates`}>{chunks}</a>,
          subscribeHere: (chunks) => (
            <a href={`${ebOrigin}/account-settings/email-preferences`}>{chunks}</a>
          ),
        })}
      </p>
      <p>{formatMessage(m.closingPara2)}</p>
      <p>
        {formatMessage(m.closingPara3, {
          getSupport: (chunks) => <a href={`${ebOrigin}/help/en-us/contact-us/`}>{chunks}</a>,
        })}
      </p>
      <p>
        {formatMessage(m.closingPara4, {
          shareFeedback: (chunks) => (
            <a href="https://ms732111.typeform.com/eventbrite">{chunks}</a>
          ),
        })}
      </p>
      <p>{formatMessage(m.closingSignoff)}</p>
      <p>{PRODUCT_ROADMAP_AUTHOR.firstName}</p>
    </>
  );
};

function renderProductRoadmapSectionBody(
  key: ProductRoadmapSectionKey,
  formatMessage: IntlShape['formatMessage'],
  ebOrigin: string,
): React.ReactNode {
  const m = productRoadmapMessages;

  switch (key) {
    case 'whatComesNext':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section0Para1} />
          <P formatMessage={formatMessage} msg={m.section0Para2} />
          <P formatMessage={formatMessage} msg={m.section0Para3} />
        </>
      );
    case 'acceleratedDev':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section1Para1} />
          <P formatMessage={formatMessage} msg={m.section1Para2} />
        </>
      );
    case 'platformReliability':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section2Para1} />
          <p>{formatMessage(m.alreadyLive)}</p>
          {renderProductRoadmapRichText(formatMessage(m.section2Bullets) as string)}
        </>
      );
    case 'creatorTools':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section3Para1} />
          <p>{formatMessage(m.alreadyLive)}</p>
          {renderProductRoadmapRichText(formatMessage(m.section3Bullets) as string)}
        </>
      );
    case 'eventReach':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section4Para1} />
          <p>{formatMessage(m.alreadyLive)}</p>
          {renderProductRoadmapRichText(formatMessage(m.section4Bullet1) as string)}
          <ProductRoadmapSectionVisual sectionKey="eventReach" visualIndex={0} />
          {renderProductRoadmapRichText(formatMessage(m.section4Bullet2) as string)}
          <ProductRoadmapSectionVisual sectionKey="eventReach" visualIndex={1} />
          {renderProductRoadmapRichText(formatMessage(m.section4Bullet3) as string)}
          <ProductRoadmapSectionVisual sectionKey="eventReach" visualIndex={2} />
          {renderProductRoadmapRichText(formatMessage(m.section4Bullet4) as string)}
        </>
      );
    case 'ticketing':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section5Para1} />
          <p>{formatMessage(m.alreadyLive)}</p>
          {renderProductRoadmapRichText(formatMessage(m.section5Bullet1) as string)}
          <ProductRoadmapSectionVisual sectionKey="ticketing" visualIndex={0} />
          {renderProductRoadmapRichText(formatMessage(m.section5Bullet2) as string)}
        </>
      );
    case 'checkout':
      return (
        <>
          <P formatMessage={formatMessage} msg={m.section6Para1} />
          <p>{formatMessage(m.alreadyLive)}</p>
          {renderProductRoadmapRichText(formatMessage(m.section6Bullets) as string)}
        </>
      );
    case 'closing':
      return <ProductRoadmapClosingBody ebOrigin={ebOrigin} />;
  }
}

export interface ProductRoadmapPageProps {
  env: Env;
}

export const ProductRoadmapPage: React.FC<ProductRoadmapPageProps> = ({ env }) => {
  const navigation = NAVBAR_FALLBACK;
  const { formatMessage } = useIntl();
  const ebOrigin = `https://${env.ebHost}`;

  const breadcrumbItems = useMemo(
    () => [
      { label: formatMessage(productRoadmapMessages.breadcrumbHome), link: `${ebOrigin}/` },
      {
        label: formatMessage(productRoadmapMessages.breadcrumbProductUpdates),
        link: `${ebOrigin}/product-updates`,
      },
      {
        label: formatMessage(productRoadmapMessages.breadcrumbCurrent),
        link: `${ebOrigin}/${PRODUCT_ROADMAP_2026_SEGMENT}/`,
        isCurrentPage: true,
      },
    ],
    [formatMessage, ebOrigin],
  );

  const sectionEntries = Object.entries(productRoadmapSections) as [
    ProductRoadmapSectionKey,
    (typeof productRoadmapSections)[ProductRoadmapSectionKey],
  ][];

  return (
    <div className={styles.page}>
      <Navbar {...navigation} />
      <main id="main-content" className={styles.main}>
        <div className={styles.breadcrumbsBand}>
          <Breadcrumbs density={BREADCRUMBS_DENSITY_OPTIONS.COMPACT} items={breadcrumbItems} />
        </div>

        <div className={styles.heroOuter}>
          <section className={styles.hero} aria-labelledby="pr-hero-title">
            <div className={styles.heroCard}>
              <div className={styles.heroCopy}>
                <h1 id="pr-hero-title" className={styles.heroTitle}>
                  {formatMessage(productRoadmapMessages.heroTitle)}
                </h1>
                <div className={styles.heroDek}>
                  <p>{formatMessage(productRoadmapMessages.heroDek, AUTHOR_VALUES)}</p>
                </div>
              </div>
              <div className={styles.heroImageWrap}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={PRODUCT_ROADMAP_ASSETS.heroImage}
                  alt=""
                  className={styles.heroImage}
                  width={608}
                  height={304}
                  fetchPriority="high"
                />
              </div>
            </div>
          </section>
        </div>

        <div className={styles.shell}>
          <div className={styles.contentColumn}>
            <div className={styles.introModule}>
              <div className={styles.introRow}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={PRODUCT_ROADMAP_ASSETS.avatar}
                  alt=""
                  className={styles.avatarImg}
                  width={40}
                  height={40}
                />
                <Typography as="p" variant="body-md" className={styles.byline}>
                  {formatMessage(productRoadmapMessages.introBylinePrefix)}{' '}
                  <strong className={styles.bylineStrong}>{AUTHOR_VALUES.authorFullName}</strong>
                </Typography>
              </div>
              <div className={styles.richText}>
                <P
                  formatMessage={formatMessage}
                  msg={productRoadmapMessages.introPara1}
                  values={AUTHOR_VALUES}
                />
                <P formatMessage={formatMessage} msg={productRoadmapMessages.introPara2} />
                <P formatMessage={formatMessage} msg={productRoadmapMessages.introPara3} />
                <P formatMessage={formatMessage} msg={productRoadmapMessages.introPara4} />
              </div>
            </div>

            {sectionEntries.map(([key, pair]) => {
              const hasTitle = 'title' in pair;
              return (
                <article
                  key={key}
                  className={styles.sectionModule}
                  aria-labelledby={hasTitle ? `pr-section-${key}` : undefined}
                >
                  {hasTitle ? (
                    <Typography
                      as="h2"
                      variant={HEADING_MD_SECTIONS.has(key) ? 'heading-md' : 'heading-sm'}
                      id={`pr-section-${key}`}
                      className={styles.sectionTitle}
                    >
                      {formatMessage(pair.title)}
                    </Typography>
                  ) : null}
                  <div className={styles.richText}>
                    {renderProductRoadmapSectionBody(key, formatMessage, ebOrigin)}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </main>
      <Footer env={env} />
    </div>
  );
};
